import React from 'react';
import { cn } from '@/lib/utils';
import { UserCog, Calculator, Eye, ShoppingCart, Crown, User } from 'lucide-react';
import type { MemberRole } from '@/types/client';
import { MEMBER_ROLE_LABELS } from '@/types/client';

interface MemberRoleBadgeProps {
  role: MemberRole | string;
  showIcon?: boolean;
  variant?: 'default' | 'compact';
  className?: string;
}

const MEMBER_ROLE_CONFIG: Record<MemberRole, { label: string; icon: any; bgColor: string; textColor: string }> = {
  // Nouveaux types
  comptable: {
    label: MEMBER_ROLE_LABELS.comptable,
    icon: Calculator,
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    textColor: 'text-blue-700 dark:text-blue-300',
  },
  responsable: {
    label: MEMBER_ROLE_LABELS.responsable,
    icon: UserCog,
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    textColor: 'text-purple-700 dark:text-purple-300',
  },
  gerant: {
    label: MEMBER_ROLE_LABELS.gerant,
    icon: Crown,
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    textColor: 'text-green-700 dark:text-green-300',
  },
  employe: {
    label: MEMBER_ROLE_LABELS.employe,
    icon: Eye,
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    textColor: 'text-gray-700 dark:text-gray-300',
  },
  // Anciens types pour compatibilité
  manager: {
    label: MEMBER_ROLE_LABELS.manager,
    icon: UserCog,
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    textColor: 'text-purple-700 dark:text-purple-300',
  },
  accountant: {
    label: MEMBER_ROLE_LABELS.accountant,
    icon: Calculator,
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    textColor: 'text-blue-700 dark:text-blue-300',
  },
  viewer: {
    label: MEMBER_ROLE_LABELS.viewer,
    icon: Eye,
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    textColor: 'text-gray-700 dark:text-gray-300',
  },
  purchaser: {
    label: MEMBER_ROLE_LABELS.purchaser,
    icon: ShoppingCart,
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    textColor: 'text-orange-700 dark:text-orange-300',
  },
  independent: {
    label: MEMBER_ROLE_LABELS.independent,
    icon: Crown,
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    textColor: 'text-green-700 dark:text-green-300',
  },
};

export const MemberRoleBadge: React.FC<MemberRoleBadgeProps> = ({
  role,
  showIcon = true,
  variant = 'default',
  className = '',
}) => {
  const config = MEMBER_ROLE_CONFIG[role as MemberRole];

  // Handle dynamic/custom roles - display with default styling
  if (!config) {
    // Capitalize first letter of the role for display
    const displayLabel = role ? role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ') : 'Inconnu';

    const defaultClasses = cn(
      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
      'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
      'hover:scale-105 hover:shadow-sm transition-all duration-200',
      className
    );

    return (
      <span className={variant === 'compact' ? cn(
        'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium',
        'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
        className
      ) : defaultClasses}>
        {showIcon && <User className="h-3 w-3 flex-shrink-0" />}
        <span className="font-medium">{displayLabel}</span>
      </span>
    );
  }

  const Icon = config.icon;

  const baseClasses = cn(
    'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-all duration-200',
    config.bgColor,
    config.textColor,
    'hover:scale-105 hover:shadow-sm',
    className
  );

  const compactClasses = cn(
    'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium',
    config.bgColor,
    config.textColor,
    className
  );

  const classes = variant === 'compact' ? compactClasses : baseClasses;

  return (
    <span className={classes}>
      {showIcon && <Icon className="h-3 w-3 flex-shrink-0" />}
      <span className="font-medium">{config.label}</span>
    </span>
  );
};

export default MemberRoleBadge;
